/**
 * Secure math.js configuration for derived field evaluation.
 *
 * Creates a sandboxed math.js instance with:
 * - Dangerous functions disabled (import, evaluate, parse, etc.)
 * - Custom date functions, all backed by dayjs with the `advancedFormat`,
 *   `isoWeek` and `quarterOfYear` plugins. The module extends dayjs at load
 *   time so the sandbox is self-contained — tests work without app bootstrapping.
 */
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { all, create } from 'mathjs';

import { parseDate } from '#core/lib/utils/date.ts';

import { extractDatapointValue } from '#plugin-chat/lib/timeseries/datapoint-value.ts';

// `dayjs.extend` is idempotent, so registering here plays nicely with any
// other entry point that also extends these plugins.
// - `advancedFormat` adds the `GGGG` (ISO week-year) and `WW` (ISO week)
//   tokens consumed by `formatDate(_, 'week')`.
// - `isoWeek` adds `.isoWeek()`, `.isoWeekday()` and `startOf('isoWeek')`.
// - `quarterOfYear` adds `.quarter()` and `startOf('quarter')`.
dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

// Create isolated math.js instance
// Note: `all` should always be defined when imported from mathjs, but TypeScript types mark it as potentially undefined
if (!all) {
  throw new Error('mathjs `all` export is undefined - this should never happen');
}
const math = create(all);

/**
 * Format a value's type for error messages emitted from custom math.js
 * operators. Distinguishes `null`, `array`, `Date`, and primitive types so
 * the LLM sees the actual shape mismatch behind a wrong-type call rather
 * than a generic "got object".
 *
 * Exported for reuse by the timeseries fetcher closures in
 * `timeseries-prefetch.ts`, which throw with the same message format.
 */
export function describeType(v: unknown): string {
  if (v === null) {
    return 'null';
  }
  if (Array.isArray(v)) {
    return 'array';
  }
  if (v instanceof Date) {
    return 'Date';
  }
  return typeof v;
}

/**
 * Input guard shared by the array-accepting custom operators (`values`,
 * `timestamps`, `first`, `last`). Returns the array on success, `null` for
 * null/undefined input (caller decides the default — `[]` for accumulating
 * operators, `null` for picking operators), and throws on any other shape.
 *
 * Centralising here so the wrong-type message format stays uniform across
 * operators — a future change to the error string only touches one place.
 */
function ensureArrayInput(arr: unknown, fnName: string): unknown[] | null {
  if (arr === null || arr === undefined) {
    return null;
  }
  if (!Array.isArray(arr)) {
    throw new TypeError(`${fnName} expected an array, got ${describeType(arr)}`);
  }
  return arr;
}

// Custom operators distinguish three input cases by design:
//  - `null` / `undefined` inputs → graceful pass-through (`null` or `[]`),
//    matching the rest of the pipeline's "missing data → missing output"
//    convention. These are legitimate sparse-data flows, not LLM errors.
//  - Wrong-shape inputs (e.g. `strlen(123)`, `prop(42, "k")`,
//    `values("foo")`) → throw a `TypeError` so the LLM sees the shape
//    mismatch in `evaluateCompiledField`'s error envelope and can correct
//    its expression. The previous null-collapse hid these mistakes and
//    let the LLM infer "no data exists" from a typo.
//  - Empty arrays / absent keys → graceful (`null` / `[]`), since these
//    are valid downstream signals from upstream operators.
math.import({
  /**
   * Returns the length of a string.
   * @returns String length, or null for null/undefined input.
   * @throws TypeError if input is non-null and not a string.
   */
  strlen: (s: unknown): number | null => {
    if (s === null || s === undefined) {
      return null;
    }
    if (typeof s !== 'string') {
      throw new TypeError(`strlen expected a string, got ${describeType(s)}`);
    }
    return s.length;
  },

  /**
   * Extracts a substring from a string.
   * @param s - Input string
   * @param start - Start index (0-based)
   * @param len - Optional length of substring to extract
   * @returns Substring, or null for null/undefined input.
   * @throws TypeError if input is non-null and not a string.
   */
  substr: (s: unknown, start: number, len?: number): string | null => {
    if (s === null || s === undefined) {
      return null;
    }
    if (typeof s !== 'string') {
      throw new TypeError(`substr expected a string, got ${describeType(s)}`);
    }
    return s.substring(start, len === undefined ? undefined : start + len);
  },

  /**
   * Concatenates multiple values into a single string. A null/undefined
   * arg yields a null result (one-input-missing → output-missing); a
   * wrong-typed arg throws.
   *
   * @returns Concatenated string, or null if any arg is null/undefined.
   * @throws TypeError if any non-null arg is neither string nor number.
   */
  strcat: (...args: unknown[]): string | null => {
    for (const a of args) {
      if (a === null || a === undefined) {
        return null;
      }
      if (typeof a !== 'string' && typeof a !== 'number') {
        throw new TypeError(`strcat expected string or number args, got ${describeType(a)}`);
      }
    }
    return args.join('');
  },

  /**
   * Accesses a property of an object by key.
   * Enables expressions like `prop(wellString, "externalId")` to extract nested values.
   *
   * @returns The property value (with `undefined` coerced to `null`), or
   *   null when `obj` is null/undefined or the key is absent. Both are
   *   legitimate "missing data" signals downstream.
   * @throws TypeError if `obj` is non-null and not a plain object (e.g.
   *   a number, string, or array).
   */
  prop: (obj: unknown, key: string): unknown => {
    if (obj === null || obj === undefined) {
      return null;
    }
    if (typeof obj !== 'object' || Array.isArray(obj)) {
      throw new TypeError(`prop expected a plain object, got ${describeType(obj)}`);
    }
    if (!(key in (obj as Record<string, unknown>))) {
      return null;
    }
    return (obj as Record<string, unknown>)[key] ?? null;
  },
});

// Register custom date functions BEFORE disabling import
math.import({
  /**
   * Calculate days between two dates.
   * @param d1 - First date string (ISO 8601)
   * @param d2 - Second date string (ISO 8601)
   * @returns Integer number of days (d2 - d1), floored
   */
  daysBetween: (d1: string, d2: string): number =>
    // `Math.floor` on the fractional diff preserves the original contract:
    // dayjs's integer `.diff` truncates toward zero, which would drift for
    // sub-day negative differences.
    Math.floor(parseDate(d2).diff(parseDate(d1), 'day', true)),

  /**
   * Extract year from a date string.
   * @param d - Date string (ISO 8601)
   * @returns Four-digit year
   */
  extractYear: (d: string): number => parseDate(d).year(),

  /**
   * Extract month from a date string.
   * @param d - Date string (ISO 8601)
   * @returns Month number (1-12)
   */
  extractMonth: (d: string): number => parseDate(d).month() + 1,

  /**
   * Extract quarter from a date string.
   * @param d - Date string (ISO 8601)
   * @returns Quarter number (1-4)
   */
  extractQuarter: (d: string): number => parseDate(d).quarter(),

  /**
   * Extract day-of-month from a date string.
   * @param d - Date string (ISO 8601)
   * @returns Day of month (1-31)
   */
  extractDay: (d: string): number => parseDate(d).date(),

  /**
   * Extract ISO 8601 week number from a date string.
   * Weeks start on Monday; week 1 contains the first Thursday of the year.
   * @param d - Date string (ISO 8601)
   * @returns ISO week number (1-53)
   */
  extractWeek: (d: string): number => parseDate(d).isoWeek(),

  /**
   * Extract ISO 8601 day-of-week from a date string.
   * @param d - Date string (ISO 8601)
   * @returns Day of week (1=Monday, 7=Sunday)
   */
  extractDayOfWeek: (d: string): number => parseDate(d).isoWeekday(),

  /**
   * Extract hour-of-day from a date string.
   * @param d - Date string (ISO 8601)
   * @returns Hour (0-23)
   */
  extractHour: (d: string): number => parseDate(d).hour(),

  /**
   * Format a date as a truncated ISO-like string suitable for grouping.
   * Returns a zero-padded, sortable key per unit:
   * - "year"    -> "2024"
   * - "quarter" -> "2024-Q2"
   * - "month"   -> "2024-06"
   * - "week"    -> "2024-W24" (uses ISO week + ISO week-year)
   * - "day"     -> "2024-06-15"
   * - "hour"    -> "2024-06-15T10"
   *
   * Throws if the date is invalid or the unit is not recognised.
   * @param d - Date string (ISO 8601)
   * @param unit - Truncation unit: year | quarter | month | week | day | hour
   */
  formatDate: (d: string, unit: string): string => {
    const date = parseDate(d);
    switch (unit) {
      case 'year':
        return date.format('YYYY');
      case 'quarter':
        return date.format('YYYY-[Q]Q');
      case 'month':
        return date.format('YYYY-MM');
      case 'week':
        // `GGGG` = ISO week-year, `WW` = zero-padded ISO week number.
        return date.format('GGGG-[W]WW');
      case 'day':
        return date.format('YYYY-MM-DD');
      case 'hour':
        return date.format('YYYY-MM-DD[T]HH');
      default:
        throw new Error(`Invalid unit "${unit}". Use one of: year, quarter, month, week, day, hour`);
    }
  },

  /**
   * Return the start of the ISO calendar year containing the given date.
   * @param d - Date string (ISO 8601)
   * @returns `YYYY-01-01`
   */
  startOfYear: (d: string): string => parseDate(d).startOf('year').format('YYYY-MM-DD'),

  /**
   * Return the start of the calendar quarter containing the given date.
   * @param d - Date string (ISO 8601)
   * @returns `YYYY-MM-01` where MM is the first month of the quarter (01, 04, 07, 10)
   */
  startOfQuarter: (d: string): string => parseDate(d).startOf('quarter').format('YYYY-MM-DD'),

  /**
   * Return the start of the calendar month containing the given date.
   * @param d - Date string (ISO 8601)
   * @returns `YYYY-MM-01`
   */
  startOfMonth: (d: string): string => parseDate(d).startOf('month').format('YYYY-MM-DD'),

  /**
   * Return the ISO Monday of the ISO week containing the given date.
   * @param d - Date string (ISO 8601)
   * @returns `YYYY-MM-DD` of Monday of the same ISO week
   */
  startOfWeek: (d: string): string => parseDate(d).startOf('isoWeek').format('YYYY-MM-DD'),

  /**
   * Return the start of the day containing the given date.
   * @param d - Date string (ISO 8601)
   * @returns `YYYY-MM-DD`
   */
  startOfDay: (d: string): string => parseDate(d).startOf('day').format('YYYY-MM-DD'),

  /**
   * Return the start of the hour containing the given date.
   * Uses the same local-time convention as {@link extractHour} and
   * `formatDate(_, 'hour')`; the returned string is a valid ISO local-time
   * timestamp that round-trips through `new Date(...)`.
   * @param d - Date string (ISO 8601)
   * @returns `YYYY-MM-DDTHH:00:00`
   */
  startOfHour: (d: string): string => parseDate(d).startOf('hour').format('YYYY-MM-DD[T]HH:00:00'),
});

// Register timeseries-helper functions BEFORE disabling import. These are
// pure helpers — no closure over fetched data — so they live in the static
// sandbox alongside `prop`, `strlen`, etc. The dynamic fetcher functions
// (datapoints, hourly, daily, weekly, monthly) close over per-evaluation
// prefetch results and are injected into the row scope by
// `evaluateDerivedFields` instead.
//
// Field-resolution shared with `transformToChartFormat` via the
// `extractDatapointValue` helper (single SSOT in `lib/timeseries`).
math.import({
  /**
   * Extract the numeric value from a single datapoint object. Accepts both
   * raw (`{timestamp, value}`) and aggregated (`{timestamp, average}` etc.)
   * shapes; finite numbers pass through unchanged so a chain that already
   * extracted values still type-checks.
   *
   * @returns The first finite numeric field present, or `null` for
   *   null/undefined input or an object with no usable numeric field.
   * @throws TypeError if input is non-null, non-numeric, and not a plain
   *   object (e.g. a string or array).
   */
  value: (dp: unknown): number | null => {
    if (dp === null || dp === undefined) {
      return null;
    }
    if (typeof dp === 'number') {
      return Number.isFinite(dp) ? dp : null;
    }
    if (typeof dp !== 'object' || Array.isArray(dp)) {
      throw new TypeError(`value expected a datapoint object or number, got ${describeType(dp)}`);
    }
    return extractDatapointValue(dp);
  },

  /**
   * Extract the numeric values from a datapoint array, dropping elements
   * that lack a usable numeric field. Use as the conversion step before a
   * math.js reducer — e.g. `mean(values(monthly(rateRef)))`.
   *
   * @returns A finite-number array (possibly empty), or `[]` for
   *   null/undefined input.
   * @throws TypeError if input is non-null and not an array.
   */
  values: (arr: unknown): number[] => {
    const valid = ensureArrayInput(arr, 'values');
    if (valid === null) {
      return [];
    }
    const out: number[] = [];
    for (const dp of valid) {
      const v = extractDatapointValue(dp);
      if (v !== null) {
        out.push(v);
      }
    }
    return out;
  },

  /**
   * Extract the timestamps from a datapoint array. Element type mirrors
   * whatever CDF returned (typically `Date`); non-datapoint elements are
   * silently dropped.
   *
   * @returns The aligned timestamp array (possibly empty), or `[]` for
   *   null/undefined input.
   * @throws TypeError if input is non-null and not an array.
   */
  timestamps: (arr: unknown): Array<Date | string | number> => {
    const valid = ensureArrayInput(arr, 'timestamps');
    if (valid === null) {
      return [];
    }
    const out: Array<Date | string | number> = [];
    for (const dp of valid) {
      if (dp === null || typeof dp !== 'object') {
        continue;
      }
      const ts = (dp as { timestamp?: unknown }).timestamp;
      if (ts instanceof Date || typeof ts === 'string' || typeof ts === 'number') {
        out.push(ts);
      }
    }
    return out;
  },

  /**
   * Return the first element of an array. Math.js doesn't provide this;
   * reach for it when you need the earliest datapoint — e.g.
   * `first(monthly(rateRef))` is the earliest monthly aggregate,
   * `value(first(...))` is its numeric value.
   *
   * @returns The first element (with `undefined` coerced to `null`), or
   *   `null` for empty / null / undefined input.
   * @throws TypeError if input is non-null and not an array.
   */
  first: (arr: unknown): unknown => {
    const valid = ensureArrayInput(arr, 'first');
    if (valid === null || valid.length === 0) {
      return null;
    }
    return valid[0] ?? null;
  },

  /**
   * Return the last element of an array. Math.js doesn't provide this;
   * pair with `value()` to read the latest scalar — e.g.
   * `value(last(datapoints(rateRef)))`.
   *
   * @returns The last element (with `undefined` coerced to `null`), or
   *   `null` for empty / null / undefined input.
   * @throws TypeError if input is non-null and not an array.
   */
  last: (arr: unknown): unknown => {
    const valid = ensureArrayInput(arr, 'last');
    if (valid === null || valid.length === 0) {
      return null;
    }
    return valid.at(-1) ?? null;
  },
});

// Disabled functions list - these could be exploited to escape the sandbox
// IMPORTANT: This must come AFTER registering custom functions
// NOTE: We cannot disable 'parse' because math.compile() uses it internally
const DISABLED_FUNCTIONS = ['import', 'createUnit', 'evaluate', 'simplify', 'derivative', 'resolve', 'reviver'];

// Override dangerous functions with errors
math.import(
  Object.fromEntries(
    DISABLED_FUNCTIONS.map((fn) => [
      fn,
      () => {
        throw new Error(`Function ${fn} is disabled for security`);
      },
    ]),
  ),
  { override: true },
);

export { math };
